import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import axios from 'axios';

const AuthContext = createContext();

// Configuración base de axios
//const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://admin.horebapps.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para agregar token automáticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('horeb_admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('horeb_admin_token');
      localStorage.removeItem('horeb_admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verificar autenticación al cargar la app
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('horeb_admin_token');
      const savedUser = localStorage.getItem('horeb_admin_user');

      if (token && savedUser) {
        // Verificar que el token siga siendo válido
        const response = await api.get('/api/auth/verificar-token');
        
        if (response.data.valid) {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setIsAuthenticated(true);

          // Si el usuario requiere cambio de contraseña, mantener el estado
          if (response.data.user.requiere_cambio_password) {
            setUser(prev => ({
              ...prev,
              requiere_cambio_password: true
            }));
          }
        } else {
          throw new Error('Token inválido');
        }
      }
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      logout(false); // Logout silencioso
    } finally {
      setLoading(false);
    }
  };

  const login = async (usuario, password) => {
    try {
      setLoading(true);
      
      const response = await api.post('/api/auth/login', {
        usuario,
        password
      });

      const { token, user: userData } = response.data;

      // Guardar en localStorage
      localStorage.setItem('horeb_admin_token', token);
      localStorage.setItem('horeb_admin_user', JSON.stringify(userData));

      // Actualizar estado
      setUser(userData);
      setIsAuthenticated(true);

      message.success('¡Bienvenido al Portal Administrativo!');

      return userData;
    } catch (error) {
      console.error('Error en login:', error);
      
      const errorMessage = error.response?.data?.error || 'Error al iniciar sesión';
      
      // Mensajes específicos según el tipo de error
      if (error.response?.status === 429) {
        message.error('Demasiados intentos de login. Intenta más tarde.');
      } else if (error.response?.status === 401) {
        message.error('Credenciales incorrectas');
      } else {
        message.error(errorMessage);
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (showMessage = true) => {
    try {
      // Intentar logout en el servidor
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Error en logout del servidor:', error);
    } finally {
      // Limpiar estado local siempre
      localStorage.removeItem('horeb_admin_token');
      localStorage.removeItem('horeb_admin_user');
      setUser(null);
      setIsAuthenticated(false);
      
      if (showMessage) {
        message.success('Sesión cerrada correctamente');
      }
    }
  };

  const cambiarPassword = async (passwordActual, passwordNueva) => {
    try {
      setLoading(true);
      
      await api.post('/api/auth/cambiar-password', {
        passwordActual,
        passwordNueva
      });

      // Actualizar estado del usuario para quitar el requerimiento de cambio
      setUser(prev => ({
        ...prev,
        requiere_cambio_password: false
      }));

      // Actualizar localStorage
      const updatedUser = {
        ...user,
        requiere_cambio_password: false
      };
      localStorage.setItem('horeb_admin_user', JSON.stringify(updatedUser));

      message.success('Contraseña actualizada correctamente');
      
      return true;
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      
      const errorMessage = error.response?.data?.error || 'Error al cambiar contraseña';
      message.error(errorMessage);
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar permisos
  const hasPermission = (recurso, accion) => {
    if (!user || !user.permisos) return false;
    
    // Super admin tiene todos los permisos
    if (user.rol === 'super_admin') return true;
    
    // Verificar permisos específicos
    const permisos = user.permisos;
    return permisos[recurso] && permisos[recurso][accion];
  };

  // Función para verificar roles
  const hasRole = (roles) => {
    if (!user) return false;
    
    const allowedRoles = Array.isArray(roles) ? roles : [roles];
    return allowedRoles.includes(user.rol);
  };

  // Función para obtener información del usuario actual
  const getCurrentUser = () => {
    return user;
  };

  // Función para refrescar datos del usuario
  const refreshUser = async () => {
    try {
      const response = await api.get('/api/auth/verificar-token');
      
      if (response.data.valid && response.data.user) {
        const updatedUser = response.data.user;
        setUser(updatedUser);
        localStorage.setItem('horeb_admin_user', JSON.stringify(updatedUser));
        return updatedUser;
      }
    } catch (error) {
      console.error('Error refrescando usuario:', error);
      throw error;
    }
  };

  const value = {
    // Estado
    user,
    loading,
    isAuthenticated,
    
    // Acciones de autenticación
    login,
    logout,
    cambiarPassword,
    checkAuthStatus,
    refreshUser,
    
    // Utilidades de permisos
    hasPermission,
    hasRole,
    getCurrentUser,
    
    // Instancia de axios configurada
    api
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  
  return context;
};

// Hook para verificar si está autenticado (útil para rutas)
export const useRequireAuth = () => {
  const { isAuthenticated, loading } = useAuth();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      window.location.href = '/login';
    }
  }, [isAuthenticated, loading]);
  
  return { isAuthenticated, loading };
};

// Hook para verificar permisos específicos
export const usePermissions = (recurso, accion) => {
  const { hasPermission, user, loading } = useAuth();
  
  return {
    hasPermission: hasPermission(recurso, accion),
    user,
    loading
  };
};

export default AuthContext;